/**
 * Taste Profile Service
 *
 * Manages the user's learned preferences. The AI chat backend reads
 * and writes to this profile automatically, but the frontend can also
 * read it (e.g., to pre-fill hotel brand preference) and let the user
 * edit explicit preferences (dietary restrictions, travel credentials).
 */

import { API_BASE_URL } from '../config/environment';
import { authFetch } from '../utils/authFetch';

const TASTE_PROFILE_URL = `${API_BASE_URL}/taste-profile`;

/**
 * Get the current user's taste profile.
 * Returns the full profile object, or null if not yet created.
 */
export async function getTasteProfile() {
  try {
    const response = await authFetch(TASTE_PROFILE_URL);
    if (!response.ok) return null;
    const data = await response.json();
    return data.profile || null;
  } catch (error) {
    console.warn('Failed to fetch taste profile:', error.message);
    return null;
  }
}

/**
 * Update explicit user preferences (dietary restrictions, travel credentials).
 * This is for user-initiated edits, not AI learning.
 *
 * @param {Object} updates - Partial profile: { dining: {...}, hotels: {...}, travel: {...} }
 */
export async function updateTasteProfile(updates) {
  const response = await authFetch(TASTE_PROFILE_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    throw new Error(`Failed to update taste profile: ${response.status}`);
  }
  return response.json();
}

/**
 * Send a learning signal to the taste profile.
 * Called by the frontend after a booking or search action.
 *
 * @param {string} type - Signal type (dining_booking, hotel_booking, etc.)
 * @param {Object} data - Signal data
 */
export async function learnSignal(type, data) {
  try {
    await authFetch(`${TASTE_PROFILE_URL}/learn`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, data }),
    });
  } catch (error) {
    // Non-fatal — don't break the user flow
    console.warn('Taste profile learn signal failed:', error.message);
  }
}

/**
 * Helper: extract the user's top dining preferences as a summary string.
 * Useful for displaying in the profile page.
 */
export function summarizeDiningPreferences(profile) {
  if (!profile?.dining) return 'No dining preferences yet';

  const parts = [];
  const topCuisines = getTopN(profile.dining.cuisines, 3);
  const topVibes = getTopN(profile.dining.vibes, 2);

  if (topCuisines.length > 0) parts.push(`Likes: ${topCuisines.join(', ')}`);
  if (topVibes.length > 0) parts.push(`Vibe: ${topVibes.join(', ')}`);
  if (profile.dining.dietaryRestrictions?.length > 0) {
    parts.push(`Dietary: ${profile.dining.dietaryRestrictions.join(', ')}`);
  }

  return parts.length > 0 ? parts.join(' · ') : 'No dining preferences yet';
}

/**
 * Helper: extract the user's top hotel preferences as a summary string.
 */
export function summarizeHotelPreferences(profile) {
  if (!profile?.hotels) return 'No hotel preferences yet';

  const parts = [];
  const topBrands = getTopN(profile.hotels.brands, 2);

  if (topBrands.length > 0) parts.push(`Brands: ${topBrands.join(', ')}`);
  if (profile.hotels.budgetTier) parts.push(`Budget: ${profile.hotels.budgetTier}`);

  return parts.length > 0 ? parts.join(' · ') : 'No hotel preferences yet';
}

function getTopN(freqMap, n) {
  if (!freqMap || typeof freqMap !== 'object') return [];
  return Object.entries(freqMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([key]) => key);
}
